import type { AssetKey } from "../assets/AssetManifest";
import type { AssetMap } from "../assets/loader";

export type CollectKind =
  | "drumstick"
  | "coin"
  | "bucket_power"
  | "joint"
  | "forty_oz"
  | "hennessy"
  | "crack";

export const POWER_UPS: CollectKind[] = ["bucket_power", "joint", "forty_oz"];
export const POWER_DOWNS: CollectKind[] = ["hennessy", "crack"];

export class Collectible {
  active = false;
  kind: CollectKind = "coin";
  x = 0;
  y = 0;
  w = 32;
  h = 32;
  value = 25;
  phase = 0;
  asset: AssetKey = "item.coin";

  spawn(kind: CollectKind, x: number, y: number): void {
    this.active = true;
    this.kind = kind;
    this.x = x;
    this.y = y;
    this.phase = Math.random() * Math.PI * 2;

    switch (kind) {
      case "drumstick":
        this.w = 40;
        this.h = 40;
        this.value = 75;
        this.asset = "item.drumstick";
        break;
      case "coin":
        this.w = 34;
        this.h = 34;
        this.value = 25;
        this.asset = "item.coin";
        break;
      case "bucket_power":
        this.w = 38;
        this.h = 42;
        this.value = 0;
        this.asset = "item.bucket_power";
        break;
      case "joint":
        this.w = 36;
        this.h = 36;
        this.value = 0;
        this.asset = "item.joint";
        break;
      case "forty_oz":
        this.w = 32;
        this.h = 44;
        this.value = 0;
        this.asset = "item.forty_oz";
        break;
      case "hennessy":
        this.w = 32;
        this.h = 44;
        this.value = 0;
        this.asset = "item.hennessy";
        break;
      case "crack":
        this.w = 28;
        this.h = 28;
        this.value = 0;
        this.asset = "item.crack";
        break;
    }
  }

  update(dt: number): void {
    if (!this.active) return;
    this.phase += dt * 6;
  }

  offscreen(camX: number): boolean {
    return this.x < camX - 60;
  }

  get hitbox(): { x: number; y: number; w: number; h: number } {
    return {
      x: this.x - this.w / 2 + 4,
      y: this.y - this.h / 2 + 4,
      w: this.w - 8,
      h: this.h - 8,
    };
  }

  get isPowerUp(): boolean {
    return POWER_UPS.includes(this.kind);
  }

  get isPowerDown(): boolean {
    return POWER_DOWNS.includes(this.kind);
  }

  draw(ctx: CanvasRenderingContext2D, camX: number, assets: AssetMap): void {
    if (!this.active) return;
    const img = assets[this.asset];
    const bob = Math.sin(this.phase) * 5;
    const sx = this.x - camX;
    const sy = this.y + bob;

    if (this.kind === "coin") {
      const pulse = 0.35 + Math.sin(this.phase * 2) * 0.15;
      ctx.save();
      ctx.globalAlpha = pulse;
      ctx.fillStyle = "#ffe566";
      ctx.beginPath();
      ctx.arc(sx, sy, this.w * 0.7, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // Power-up glow
    if (this.isPowerUp) {
      const pulse = 0.3 + Math.sin(this.phase * 3) * 0.15;
      ctx.save();
      ctx.globalAlpha = pulse;
      ctx.fillStyle = "#44ff88";
      ctx.beginPath();
      ctx.arc(sx, sy, this.w * 0.65, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    // Power-down dark aura
    if (this.isPowerDown) {
      const pulse = 0.2 + Math.sin(this.phase * 2) * 0.1;
      ctx.save();
      ctx.globalAlpha = pulse;
      ctx.fillStyle = "#aa00ff";
      ctx.beginPath();
      ctx.arc(sx, sy, this.w * 0.6, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }

    ctx.drawImage(img, sx - this.w / 2, sy - this.h / 2, this.w, this.h);
  }
}
